import React, { useRef, useState } from 'react';
import Helmet from 'react-helmet';
import { connect, useDispatch } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import { bindActionCreators, compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import {
  Button,
  CircularProgress,
  debounce,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Toolbar,
  Typography,
} from '@material-ui/core';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

import { flattenUserRoles } from 'containers/OrganizerFormPage/flattenUserRoles';
import { selectOrganizers, selectTotal } from 'entities/organizer/selectors';
import { selectRoles } from 'entities/role/selectors';
import { selectUser, selectUserLocations } from 'entities/user/selectors';
import { TableBox } from 'retired/TableBox';
import OrganizationMenu from 'retired/OrganizationMenu';
import { primaryBlue } from 'utils/color-styles';
import { PureRender, onMount } from 'utils/component-helpers';
import { downloadFileFromJson } from 'utils/downloadFileFromJson';
import withEpics from 'utils/injectEpics';
import withReducer from 'utils/injectReducer';
import { Privileges } from 'utils/privileges';
import TablePaginationActions from 'retired/TablePaginationActions';
import Organizer from 'entities/organizer/model';
import OrganizersApi from 'entities/organizer/api';

import { ActionButtons } from './ActionButtons';
import * as Actions from './actions';
import epics from './epics';
import reducer from './reducers';
import { selectFetchParams, selectLoading } from './selectors';

const OrganizersListPage = ({
  loading,
  organizers,
  user,
  history,
  locations,
  roles,
  total,
  fetchParams,
}: OrganizersListPageProps) => {
  const [csvDownloading, setCsvDownloading] = useState(false);

  const dispatch = useDispatch();
  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleSearch = (search: string): void => {
    dispatch(Actions.setFetchParamsAction({ search }));
  };

  const handleSearchDebounced = debounce((search) => handleSearch(search), 600);

  const goToPage = (__: unknown, pageNum: number): void => {
    dispatch(Actions.setFetchParamsAction({ pageNum }));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const limit = Number(event.target.value) || 10;
    dispatch(Actions.setFetchParamsAction({ limit }));
  };

  const organizerRole = (organizer: Organizer) => {
    if (organizer.roles.size === 0) {
      return 'Blocked';
    } else {
      return flattenUserRoles(organizer.roles.toArray().map((roleId) => roles.get(roleId)))?.name;
    }
  };

  if (
    user &&
    !user.hasPrivilege(Privileges.ORGANIZATION_MANAGE_ORGANIZERS) &&
    !user.hasPrivilege(Privileges.ORGANIZATION_VIEW_ORGANIZERS)
  ) {
    history.replace('/');
  }

  const handleExportClick = async () => {
    try {
      setCsvDownloading(true);

      const resp = (await OrganizersApi.fetchRaw(user!.organization, {}).toPromise()) as {
        organizers: Record<string, unknown>[];
        total: number;
      };

      const organizers = resp.organizers.map((user) => new Organizer(user));
      const rows = organizers.map((organizer) => ({
        id: organizer.id,
        name: organizer.name,
        email: organizer.email,
        roles: organizerRole(organizer),
        locations: organizer.locations
          .map((locationId: string | undefined) => locationId && locations?.get(locationId)?.name)
          .toArray()
          .join(', '),
        createdAt: new Date(organizer.createdAt).toUTCString(),
      }));

      downloadFileFromJson(rows, `users_${new Date().toISOString()}.csv`);
    } finally {
      setCsvDownloading(false);
    }
  };

  const ExportButton = csvDownloading ? (
    <CircularProgress size={16} />
  ) : (
    <Typography
      variant="button"
      style={{ cursor: 'pointer', color: primaryBlue }}
      title="Export All Users"
      onClick={handleExportClick}
    >
      Export All Users
    </Typography>
  );

  return (
    <>
      <Helmet title="Organizers" />
      <div
        style={{
          display: 'flex',
          marginBottom: 35,
        }}
      >
        <div>
          <OrganizationMenu />
        </div>
        {user?.hasPrivilege(Privileges.ORGANIZATION_MANAGE_ORGANIZERS) && (
          <div style={{ marginLeft: 'auto' }}>
            <Link to="/organizers/new">
              <Button size="large">Create new user</Button>
            </Link>
          </div>
        )}
      </div>
      <div style={{ display: 'table', width: '100%' }}>
        <TableBox
          loading={loading}
          fixedTop={
            <Toolbar style={{ marginTop: -4 }}>
              <Typography variant="h6">Users</Typography>
              <div style={{ marginLeft: 'auto' }}>
                <TextField
                  type="search"
                  size="small"
                  placeholder="Search"
                  inputRef={searchInputRef}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="clear"
                          onClick={() => {
                            if (searchInputRef.current) {
                              searchInputRef.current.value = '';
                            }
                            handleSearchDebounced('');
                          }}
                          size="small"
                          disabled={!fetchParams.get('search')}
                        >
                          <ClearIcon fontSize="inherit" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
                    handleSearchDebounced(event.target.value)
                  }
                  style={{
                    width: 350,
                  }}
                />
              </div>
            </Toolbar>
          }
          fixedBottom={
            <>
              <div>{ExportButton}</div>
              <TablePagination
                component="div"
                count={total || 0}
                rowsPerPage={fetchParams.get('limit')}
                page={fetchParams.get('pageNum')}
                onChangePage={goToPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                SelectProps={{
                  inputProps: { 'aria-label': 'rows per page' },
                  native: true,
                }}
                onChangeRowsPerPage={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
              />
            </>
          }
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell size="medium">Name</TableCell>
                <TableCell size="medium">Email</TableCell>
                <TableCell size="medium">Role</TableCell>
                <TableCell size="medium">Locations</TableCell>
                {user?.hasPrivilege(Privileges.ORGANIZATION_MANAGE_ORGANIZERS) && (
                  <TableCell size="medium">Approve / Reject</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody
              style={{
                ...(loading && {
                  opacity: 0.3,
                  cursor: 'wait',
                  pointerEvents: 'none',
                }),
              }}
            >
              {organizers
                .map(
                  (organizer) =>
                    organizer && (
                      <TableRow key={organizer.id}>
                        <TableCell>
                          <Link to={`/organizers/${organizer.id}`}>{organizer.name}</Link>
                        </TableCell>
                        <TableCell>{organizer.email || '-'}</TableCell>
                        <TableCell>{organizerRole(organizer)}</TableCell>
                        <TableCell>
                          {organizer?.locations?.size > 0
                            ? organizer?.locations
                                .toArray()
                                .map((locationId: string) => locations?.get(locationId)?.name)
                                .join(', ')
                            : 'All Locations'}
                        </TableCell>
                        {user?.hasPrivilege(Privileges.ORGANIZATION_MANAGE_ORGANIZERS) && (
                          <TableCell>
                            {user?.id !== organizer?.id && (
                              <ActionButtons user={organizer} roles={roles} />
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    )
                )
                .toArray()}
            </TableBody>
          </Table>
        </TableBox>
      </div>
    </>
  );
};

type StateProps = {
  organizers: ReturnType<typeof selectOrganizers>;
  loading: boolean;
  user: ReturnType<typeof selectUser>;
  locations: ReturnType<typeof selectUserLocations>;
  roles: ReturnType<typeof selectRoles>;
  total: ReturnType<typeof selectTotal>;
  fetchParams: ReturnType<typeof selectFetchParams>;
};

type OwnProps = {
  history: any;
};

type OrganizersListPageProps = StateProps & OwnProps;

const withConnect = connect(
  createStructuredSelector({
    organizers: selectOrganizers,
    total: selectTotal,
    loading: selectLoading,
    user: selectUser,
    locations: selectUserLocations,
    roles: selectRoles,
    fetchParams: selectFetchParams,
  }),
  (dispatch) => ({
    actions: bindActionCreators(Actions, dispatch),
  })
);

export default compose(
  withRouter,
  withReducer({ key: 'organizersPage', reducer }),
  withEpics({ key: 'organizersPage', epics }),
  withConnect,
  PureRender,
  onMount((props: { actions: typeof Actions }) => {
    props.actions.initAction();
  })
)(OrganizersListPage);
